import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getValidToken } from "../../../utils/authUtils";
import { formatCurrency, formatDate } from "../../../utils/utils";
import { useReportFilter } from "../context/ReportFilterContext";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../../components/Loading";

interface CommissionEntry {
  tripId: string;
  incomeId: string;
  vehicleNumber: string;
  vehicleId: string;
  bookingType: string;
  journeyType: string;
  commissionAmount: number;
  commissionBookings: Array<{
    id: string;
    lSNo: string;
    lrNo: string;
    partyName: string;
    quantity: number;
    rate: string;
    amount: string;
    paid: string;
    toPay: string;
    dropLocation: string;
    luggageOperator: string;
    luggageOperatorPricingType: string;
    luggageOperatorPricingValue: string;
  }>;
  tripDetails: {
    fromLocation: string;
    toLocation: string;
    startDate: string;
    returnDate: string | null;
  };
}

interface OperatorSummary {
  name: string;
  contactNumber: string;
  totalTransactions: number;
  fixedTransactions: number;
  percentageTransactions: number;
  totalAmount: number;
  commissionAmount: number;
  totalPaid: number;
  totalToPay: number;
  dropLocation: string;
}

const calculateOperatorTotals = (operators: OperatorSummary[]) => {
  return operators.reduce(
    (acc, operator) => ({
      totalTransactions: acc.totalTransactions + operator.totalTransactions,
      fixedTransactions: acc.fixedTransactions + operator.fixedTransactions,
      percentageTransactions:
        acc.percentageTransactions + operator.percentageTransactions,
      totalAmount: acc.totalAmount + operator.totalAmount,
      commissionAmount: acc.commissionAmount + operator.commissionAmount,
      totalPaid: acc.totalPaid + operator.totalPaid,
      totalToPay: acc.totalToPay + operator.totalToPay,
    }),
    {
      totalTransactions: 0,
      fixedTransactions: 0,
      percentageTransactions: 0,
      totalAmount: 0,
      commissionAmount: 0,
      totalPaid: 0,
      totalToPay: 0,
    }
  );
};

// Helper function to calculate commission totals for a single entry
const calculateEntryTotals = (
  commissionBookings: CommissionEntry["commissionBookings"]
) => {
  return commissionBookings.reduce(
    (acc, booking) => ({
      totalAmount: acc.totalAmount + parseFloat(booking.amount),
      totalPaid: acc.totalPaid + parseFloat(booking.paid),
      totalToPay: acc.totalToPay + parseFloat(booking.toPay),
      totalQuantity: acc.totalQuantity + booking.quantity,
    }),
    {
      totalAmount: 0,
      totalPaid: 0,
      totalToPay: 0,
      totalQuantity: 0,
    }
  );
};

const CommissionReport: React.FC = () => {
  const { filterState } = useReportFilter();
  const {
    timeFilter,
    customStartDate,
    customEndDate,
    selectedVehicles,
    pickupLocation,
    dropLocation,
    operatorNames,
  } = filterState;

  const navigate = useNavigate();
  const {
    data: reportData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "commissionReport",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicles,
      pickupLocation,
      dropLocation,
      operatorNames,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/commission/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/commission/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      if (selectedVehicles.length > 0) {
        params.vehicleNumbers = selectedVehicles;
      }
      if (pickupLocation) {
        params.pickupLocation = pickupLocation;
      }
      if (dropLocation) {
        params.dropLocation = dropLocation;
      }
      if (operatorNames) {
        params.operatorName = operatorNames;
      }

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: true,
  });

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!reportData?.data || reportData.data.entries.length === 0) {
    return (
      <Typography
        textAlign="center"
        display="flex"
        justifyContent="center"
        alignItems="center"
        variant="caption"
        sx={{
          fontSize: "0.75rem",
          color: "text.secondary",
          px: 1.5,
          pb: 1,
          backgroundColor: "white",
          width: "100%",
          height: "100%",
          minHeight: "100px",
        }}
      >
        No commission data available
      </Typography>
    );
  }

  const { data } = reportData;

  const operatorCommissionData = data?.summary?.chartSummary?.map(
    (operator: any) => ({
      name: operator.name,
      value: operator.totalAmount,
      toPay: operator.totalToPay,
      paid: operator.totalPaid,
    })
  );

  return (
    <>
      {isLoading && (
        <Loading fullScreen={false} message="Loading commission report..." />
      )}
      {isFetching && !isLoading && (
        <Loading fullScreen={false} message="Refreshing commission report..." />
      )}
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Amount</Typography>
            <Typography variant="h4" fontSize={15}>
              {formatCurrency(data.totalAmount)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Commission</Typography>
            <Typography variant="h4" fontSize={15}>
              {formatCurrency(data.totalCommission)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Paid</Typography>
            <Typography variant="h4" fontSize={15}>
              {formatCurrency(data.paid)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total To Pay</Typography>
            <Typography variant="h4" fontSize={15}>
              {formatCurrency(data.toPay)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={2}>
        {/* Operator Bar Chart */}
        {data?.summary?.chartSummary?.length > 1 && (
          <>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom fontSize={16}>
                  Booking amount by Operator
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={operatorCommissionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={10} />
                    <YAxis fontSize={10} />
                    <Tooltip
                      formatter={(value) => formatCurrency(Number(value))}
                    />
                    <Legend />
                    <Bar
                      dataKey="paid"
                      name="Paid"
                      fill="#8884d8"
                      barSize={30}
                      stackId="a"
                    />
                    <Bar
                      dataKey="toPay"
                      name="To Pay"
                      fill="#ffc658"
                      barSize={30}
                      stackId="a"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </>
        )}

        {/* Operator Summary Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom fontSize={16}>
              Operator Summary
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Operator</th>
                    <th style={tableHeaderStyle}>Pickup</th>
                    <th style={tableHeaderStyle}>Drop</th>
                    <th style={tableHeaderStyle}>Contact</th>
                    <th style={tableHeaderStyle}>Transactions</th>
                    <th style={tableHeaderStyle}>Fixed/Percentage</th>
                    <th style={tableHeaderStyle}>Paid</th>
                    <th style={tableHeaderStyle}>To Pay</th>
                    <th style={tableHeaderStyle}>Total Amount</th>
                    <th style={tableHeaderStyle}>Commission</th>
                    <th style={tableHeaderStyle}>Net Income</th>
                  </tr>
                </thead>
                <tbody>
                  {data.summary.byOperator.map((operator: any) => (
                    <tr key={operator.name + operator.contactNumber}>
                      <td style={tableCellStyle}>{operator.name}</td>
                      <td style={tableCellStyle}>
                        {operator.pickupLocation || "-"}
                      </td>
                      <td style={tableCellStyle}>
                        {operator.dropLocation || "-"}
                      </td>
                      <td style={tableCellStyle}>{operator.contactNumber}</td>
                      <td style={tableCellStyle}>
                        {operator.totalTransactions}
                      </td>
                      <td style={tableCellStyle}>
                        {operator.fixedTransactions}/
                        {operator.percentageTransactions}
                      </td>
                      <td style={tableCellStyle}>
                        {formatCurrency(operator.totalPaid)}
                      </td>
                      <td style={tableCellStyle}>
                        {formatCurrency(operator.totalToPay)}
                      </td>
                      <td style={tableCellStyle}>
                        {formatCurrency(operator.totalAmount)}
                      </td>
                      <td style={tableCellStyle}>
                        {formatCurrency(operator.commissionAmount)}
                      </td>
                      <td style={tableCellStyle}>
                        {formatCurrency(
                          operator.totalAmount - operator.commissionAmount
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      ...tableHeaderStyle,
                      fontWeight: "bold",
                    }}
                  >
                    Total:
                  </td>
                  <td style={{ ...tableHeaderStyle, fontWeight: "bold" }}>
                    {
                      calculateOperatorTotals(data.summary.byOperator)
                        .totalTransactions
                    }
                  </td>
                  <td style={{ ...tableHeaderStyle, fontWeight: "bold" }}>
                    {
                      calculateOperatorTotals(data.summary.byOperator)
                        .fixedTransactions
                    }
                    /
                    {
                      calculateOperatorTotals(data.summary.byOperator)
                        .percentageTransactions
                    }
                  </td>
                  <td style={{ ...tableHeaderStyle, fontWeight: "bold" }}>
                    {formatCurrency(
                      calculateOperatorTotals(data.summary.byOperator).totalPaid
                    )}
                  </td>
                  <td style={{ ...tableHeaderStyle, fontWeight: "bold" }}>
                    {formatCurrency(
                      calculateOperatorTotals(data.summary.byOperator)
                        .totalToPay
                    )}
                  </td>
                  <td style={{ ...tableHeaderStyle, fontWeight: "bold" }}>
                    {formatCurrency(
                      calculateOperatorTotals(data.summary.byOperator)
                        .totalAmount
                    )}
                  </td>
                  <td style={{ ...tableHeaderStyle, fontWeight: "bold" }}>
                    {formatCurrency(
                      calculateOperatorTotals(data.summary.byOperator)
                        .commissionAmount
                    )}
                  </td>
                  <td style={{ ...tableHeaderStyle, fontWeight: "bold" }}>
                    {formatCurrency(
                      calculateOperatorTotals(data.summary.byOperator)
                        .totalAmount -
                        calculateOperatorTotals(data.summary.byOperator)
                          .commissionAmount
                    )}
                  </td>
                </tr>
              </table>
            </Box>
          </Paper>
        </Grid>

        {/* Commission Records Accordion */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom fontSize={16}>
              Commission Records
            </Typography>
            <Box>
              {data.entries.map((entry: any) => {
                const totals = calculateEntryTotals(entry.commissionBookings);

                return (
                  <Accordion key={entry.incomeId}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        // backgroundColor: "#f8f9fa",
                        "& .MuiAccordionSummary-content": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        },
                      }}
                    >
                      {" "}
                      <Box sx={{ display: "flex", width: "100%", gap: 2 }}>
                        <Typography
                          sx={{
                            width: "10%",
                            fontSize: tableCellStyle.fontSize,
                            cursor: "pointer",
                            textDecoration: "underline",
                            color: "primary.main",
                          }}
                          onClick={() => {
                            navigate(
                              `/trip/daily-bus/${entry.tripId}/luggage-income-details`
                            );
                          }}
                        >
                          {entry.vehicleNumber}
                        </Typography>
                        <Typography sx={{ width: "25%", fontSize: "12px" }}>
                          {entry.tripDetails.fromLocation} -{" "}
                          {entry.tripDetails.toLocation} : {entry.journeyType}
                        </Typography>
                        <Typography sx={{ width: "15%", fontSize: "12px" }}>
                          {entry.tripDetails.startDate
                            ? formatDate(entry.tripDetails.startDate)
                            : ""}
                          {entry.tripDetails.returnDate &&
                            ` - ${formatDate(entry.tripDetails.returnDate)}`}
                        </Typography>
                        <Typography sx={{ width: "15%", fontSize: "12px" }}>
                          Commission : {formatCurrency(entry.commissionAmount)}
                        </Typography>
                        <Typography sx={{ width: "12.5%", fontSize: "12px" }}>
                          Paid : {formatCurrency(totals.totalPaid)}
                        </Typography>
                        <Typography sx={{ width: "12.5%", fontSize: "12px" }}>
                          ToPay : {formatCurrency(totals.totalToPay)}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ overflowX: "auto" }}>
                        <table
                          style={{ width: "100%", borderCollapse: "collapse" }}
                        >
                          <thead>
                            <tr>
                              <th style={tableHeaderStyle}>LS No.</th>
                              <th style={tableHeaderStyle}>LR No.</th>
                              <th style={tableHeaderStyle}>Party Name</th>
                              <th style={tableHeaderStyle}>Quantity</th>
                              <th style={tableHeaderStyle}>Rate</th>
                              <th style={tableHeaderStyle}>Amount</th>
                              <th style={tableHeaderStyle}>Operator</th>
                              <th style={tableHeaderStyle}>Commission Value</th>
                              <th style={tableHeaderStyle}>Paid</th>
                              <th style={tableHeaderStyle}>To Pay</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entry.commissionBookings.map((booking: any) => (
                              <tr key={booking.id}>
                                <td style={tableCellStyle}>
                                  {booking.lSNo || "-"}
                                </td>
                                <td style={tableCellStyle}>
                                  {booking.lrNo || "-"}
                                </td>
                                <td style={tableCellStyle}>
                                  {booking.partyName}
                                </td>
                                <td style={tableCellStyle}>
                                  {booking.quantity}
                                </td>
                                <td style={tableCellStyle}>
                                  {booking.rate === "0.00"
                                    ? "N/A"
                                    : formatCurrency(Number(booking.rate))}
                                </td>
                                <td style={tableCellStyle}>
                                  {formatCurrency(Number(booking.amount))}
                                </td>
                                <td style={tableCellStyle}>
                                  {booking.luggageOperator}
                                </td>
                                <td style={tableCellStyle}>
                                  {booking.luggageOperatorPricingValue}
                                  {booking.luggageOperatorPricingType ===
                                  "Percentage"
                                    ? "%"
                                    : "₹"}
                                </td>
                                <td style={tableCellStyle}>
                                  {formatCurrency(Number(booking.paid))}
                                </td>
                                <td style={tableCellStyle}>
                                  {formatCurrency(Number(booking.toPay))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td
                                colSpan={3}
                                style={{
                                  ...tableHeaderStyle,
                                  fontWeight: "bold",
                                }}
                              >
                                Total:
                              </td>
                              <td
                                colSpan={2}
                                style={{
                                  ...tableHeaderStyle,
                                  fontWeight: "bold",
                                }}
                              >
                                {totals.totalQuantity}
                              </td>
                              <td
                                colSpan={3}
                                style={{
                                  ...tableHeaderStyle,
                                  fontWeight: "bold",
                                }}
                              >
                                {formatCurrency(totals.totalAmount)}
                              </td>
                              <td
                                style={{
                                  ...tableHeaderStyle,
                                  fontWeight: "bold",
                                }}
                              >
                                {formatCurrency(totals.totalPaid)}
                              </td>
                              <td
                                style={{
                                  ...tableHeaderStyle,
                                  fontWeight: "bold",
                                }}
                              >
                                {formatCurrency(totals.totalToPay)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

const tableHeaderStyle = {
  padding: "12px 16px",
  textAlign: "left" as const,
  backgroundColor: "#f7f7f7",
  borderBottom: "1px solid #ddd",
  fontSize: "12px",
};

const tableCellStyle = {
  padding: "12px 16px",
  borderBottom: "1px solid #ddd",
  fontSize: "12px",
};

export default CommissionReport;
